import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaCheck } from "react-icons/fa";
import { addToGuestCart, openCartDrawer } from "../../../util/guestCartService";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;
const linkFor = (card) => `/collection/${card.category?.slug || "all"}/${card.slug}`;
// Above this many sizes or colors a dropdown reads better than a wall of chips.
const MAX_CHIPS = 8;

/** Lowest price among options that have enough stock, for "from" prices. */
const fromPrice = (options, need, fallback) => {
  const open = options.filter((option) => Number(option.qty) >= need);
  return open.length ? Math.min(...open.map((option) => Number(option.final_price))) : Number(fallback || 0);
};

function OptionPicker({ name, options, chosenId, need, label, onChoose }) {
  if (options.length > MAX_CHIPS) {
    return (
      <select
        className="form-select form-select-sm bx-select"
        value={chosenId || ""}
        onChange={(event) => onChoose(event.target.value ? Number(event.target.value) : null)}
        aria-label={`${label}: ${name}`}
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id} disabled={Number(option.qty) < need}>
            {option.title}{Number(option.qty) < need ? " — sold out" : ""}
          </option>
        ))}
      </select>
    );
  }
  return (
    <div className="bx-chips" role="group" aria-label={`${label}: ${name}`}>
      {options.map((option) => {
        const soldOut = Number(option.qty) < need;
        const active = option.id === chosenId;
        return (
          <button
            type="button"
            key={option.id}
            className={`bx-chip ${active ? "is-active" : ""}`}
            disabled={soldOut}
            aria-pressed={active}
            title={soldOut ? `${option.title} — sold out` : `${option.title} — ${money(option.final_price)}`}
            onClick={() => onChoose(active ? null : option.id)}
          >
            {option.title}
          </button>
        );
      })}
    </div>
  );
}

/** One product in a bundle or in the bought-together list. */
function ItemCard({ card, image, tag, qty = 1, price, compare, isFrom, fixedOption, options = [], chosenId, onChoose, chooseLabel, check, off }) {
  return (
    <div className={`bx-item ${off ? "is-off" : ""}`}>
      <div className="bx-media">
        <Link to={linkFor(card)} className="bx-img" title={card.name}>
          <img src={getProductImageUrl(image)} alt={card.name} loading="lazy" onError={handleProductImageError} />
        </Link>
        {tag ? <span className="bx-tag">{tag}</span> : null}
        {qty > 1 ? <span className="bx-qty">×{qty}</span> : null}
        {check ? (
          <label className="bx-check" title={check.checked ? "Remove from selection" : "Add to selection"}>
            <input type="checkbox" checked={check.checked} disabled={check.disabled} onChange={(event) => check.onChange(event.target.checked)} aria-label={card.name} />
            <span aria-hidden="true"><FaCheck size={10} /></span>
          </label>
        ) : null}
      </div>
      <div className="bx-body">
        <Link to={linkFor(card)} className="bx-name" title={card.name}>{card.name}</Link>
        <div className="bx-price">
          {compare && compare > price ? <s>{money(compare * qty)}</s> : null}
          <strong>{isFrom ? <span className="bx-from">from</span> : null}{money(price * qty)}</strong>
        </div>
        {fixedOption ? <span className="bx-fixed">{fixedOption}</span> : null}
        {options.length > 0 && onChoose ? (
          <OptionPicker name={card.name} options={options} chosenId={chosenId} need={qty} label={chooseLabel} onChoose={onChoose} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * "Frequently bought together" and bundles on the product page. A bundle's
 * discount is applied by the server at checkout once all its items are in the
 * cart; the numbers here are for display.
 *
 * product / activeVariant / hasVariantChoices: the product being viewed and
 * the option picked on the page (from useProduct).
 */
export default function FrequentlyBought({ product, activeVariant, hasVariantChoices, words }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  // Why a bundle or selection cannot be added yet, shown in that box.
  const [boxError, setBoxError] = useState({ bundle: "", fbt: "" });
  const [choices, setChoices] = useState({});
  const [fbtChoices, setFbtChoices] = useState({});

  useEffect(() => {
    if (!product?.id) return undefined;
    let active = true;
    setData(null);
    setChoices({});
    setFbtChoices({});
    axios.get(`/api/products/${product.id}/recommendations`)
      .then((res) => {
        if (!active || res.data.status !== 200) return;
        setData(res.data);
        const start = { self: true };
        (res.data.frequently_bought || []).slice(0, 3).forEach((card) => {
          start[card.id] = !card.has_variants && card.in_stock;
        });
        setPicked(start);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [product?.id]);

  const self = useMemo(() => {
    if (!product) return null;
    const price = Number(activeVariant ? activeVariant.final_price : (product.pricing?.final_price ?? product.productprices?.seeling_price ?? 0));
    return { card: product, price, ready: !hasVariantChoices || Boolean(activeVariant && Number(activeVariant.qty) > 0) };
  }, [product, activeVariant, hasVariantChoices]);

  if (!data || !self) return null;
  const bundle = (data.bundles || [])[0];
  const others = (data.frequently_bought || []).slice(0, 3);
  if (!bundle && !others.length) return null;

  const needOptions = (box, message) => setBoxError((current) => ({ ...current, [box]: message }));

  // Bundle rows: an option set by the store, or the one the shopper picks here.
  // This product's row follows the size or color chosen on the page until changed here.
  const rows = bundle ? bundle.items.map((item, index) => {
    const isSelf = item.product.id === product.id;
    const options = item.variants || [];
    let variant = item.variant || null;
    if (!variant && options.length) {
      const chosenId = choices[index] ?? (isSelf ? activeVariant?.id : null);
      variant = options.find((option) => option.id === Number(chosenId)) || null;
    }
    const needsChoice = options.length > 0 && !item.variant;
    const price = variant ? Number(variant.final_price) : (needsChoice ? fromPrice(options, item.qty, item.unit_price) : Number(item.unit_price ?? item.product.price));
    return { item, index, isSelf, options, variant, price, needsChoice };
  }) : [];
  const bundleValue = rows.reduce((sum, row) => sum + row.price * row.item.qty, 0);
  const bundleSaving = !bundle ? 0 : bundle.discount_type === "percent"
    ? bundleValue * Math.min(90, Number(bundle.discount_value) || 0) / 100
    : Math.min(bundleValue, Number(bundle.discount_value) || 0);
  const bundleIsFrom = rows.some((row) => row.needsChoice && !row.variant);
  const bundleBadge = !bundle ? "" : bundle.discount_type === "percent"
    ? `−${Number(bundle.discount_value)}%`
    : `${words.youSave} ${money(bundle.discount_value)}`;

  const addBundle = () => {
    const missing = rows.find((row) => row.needsChoice && !row.variant);
    if (missing) {
      needOptions("bundle", `Choose a size or color for ${missing.item.product.name}.`);
      return;
    }
    const soldOut = rows.find((row) => row.variant && Number(row.variant.qty) < row.item.qty);
    if (soldOut) {
      needOptions("bundle", `${soldOut.item.product.name} (${soldOut.variant.title}) is sold out. Choose another option.`);
      return;
    }
    needOptions("bundle", "");
    rows.forEach((row) => {
      addToGuestCart(row.isSelf ? product : row.item.product, row.item.qty, row.variant);
    });
    openCartDrawer();
  };

  // Bought together: each product checked on its own; sizes picked in place.
  const fbtRows = others.map((card) => {
    const options = card.variants || [];
    const variant = options.find((option) => option.id === fbtChoices[card.id]) || null;
    const price = variant ? Number(variant.final_price) : (options.length ? fromPrice(options, 1, card.price) : Number(card.price));
    const soldOut = !card.in_stock || (options.length > 0 && !options.some((option) => Number(option.qty) > 0));
    return { card, options, variant, price, soldOut, checked: Boolean(picked[card.id]) && !soldOut };
  });
  const chosenRows = fbtRows.filter((row) => row.checked);
  const selectedCount = (picked.self ? 1 : 0) + chosenRows.length;
  const selectedTotal = (picked.self ? self.price : 0) + chosenRows.reduce((sum, row) => sum + row.price, 0);
  const selectedIsFrom = chosenRows.some((row) => row.options.length && !row.variant);

  const addSelected = () => {
    if (picked.self && !self.ready) {
      needOptions("fbt", "Pick the size or color for this item first.");
      return;
    }
    const missing = chosenRows.find((row) => row.options.length && !row.variant);
    if (missing) {
      needOptions("fbt", `Choose a size or color for ${missing.card.name}.`);
      return;
    }
    if (picked.self) addToGuestCart(product, 1, hasVariantChoices ? activeVariant : null);
    needOptions("fbt", "");
    chosenRows.forEach((row) => addToGuestCart(row.card, 1, row.variant));
    if (selectedCount) openCartDrawer();
  };

  return (
    <div className="product-fbt my-5">
      {bundle ? (
        <section className="bx" aria-label={words.bundleTitle}>
          <div className="bx-head">
            <div>
              <h4 className="bx-title">{words.bundleTitle}</h4>
              <p className="bx-sub">{bundle.description || bundle.name}</p>
            </div>
            {Number(bundle.discount_value) > 0 ? <span className="bx-badge">{bundleBadge}</span> : null}
          </div>

          <div className="bx-items">
            {rows.map(({ item, index, isSelf, options, variant, price, needsChoice }) => (
              <ItemCard
                key={`${item.product.id}-${index}`}
                card={item.product}
                image={isSelf ? product : item.product.image}
                tag={isSelf ? words.thisItem : ""}
                qty={item.qty}
                price={price}
                isFrom={needsChoice && !variant}
                fixedOption={!needsChoice && variant ? variant.title : ""}
                options={needsChoice ? options : []}
                chosenId={variant?.id}
                chooseLabel={words.chooseOptions}
                onChoose={(id) => {
                  setChoices((current) => ({ ...current, [index]: id }));
                  needOptions("bundle", "");
                }}
              />
            ))}
          </div>

          {boxError.bundle && <div className="bx-error" role="alert">{boxError.bundle}</div>}
          <div className="bx-foot">
            <div className="bx-sum">
              <div className="bx-sum-prices">
                {bundleSaving > 0 ? <s>{money(bundleValue)}</s> : null}
                <strong>{bundleIsFrom ? <span className="bx-from">from</span> : null}{money(bundleValue - bundleSaving)}</strong>
              </div>
              {bundleSaving > 0 ? <span className="bx-save">{words.youSave} {money(bundleSaving)}</span> : null}
            </div>
            <button type="button" className="btn-add-to-cart bx-btn" onClick={addBundle}>{words.addBundle}</button>
          </div>
        </section>
      ) : null}

      {others.length > 0 && (
        <section className="bx" aria-label={words.fbtTitle}>
          <div className="bx-head">
            <h4 className="bx-title">{words.fbtTitle}</h4>
          </div>

          <div className="bx-items">
            <ItemCard
              card={product}
              image={product}
              tag={words.thisItem}
              price={self.price}
              fixedOption={activeVariant?.title || ""}
              off={!picked.self}
              check={{ checked: !!picked.self, onChange: (on) => setPicked((current) => ({ ...current, self: on })) }}
            />
            {fbtRows.map(({ card, options, variant, price, soldOut, checked }) => (
              <ItemCard
                key={card.id}
                card={card}
                image={card.image}
                price={price}
                compare={!options.length ? card.compare_price : null}
                isFrom={options.length > 0 && !variant}
                options={soldOut ? [] : options}
                chosenId={variant?.id}
                chooseLabel={words.chooseOptions}
                onChoose={(id) => {
                  needOptions("fbt", "");
                  setFbtChoices((current) => ({ ...current, [card.id]: id }));
                  if (id) setPicked((current) => ({ ...current, [card.id]: true }));
                }}
                off={!checked}
                fixedOption={soldOut ? "Sold out" : ""}
                check={{ checked, disabled: soldOut, onChange: (on) => setPicked((current) => ({ ...current, [card.id]: on })) }}
              />
            ))}
          </div>

          {boxError.fbt && <div className="bx-error" role="alert">{boxError.fbt}</div>}
          <div className="bx-foot">
            <div className="bx-sum">
              <div className="bx-sum-prices">
                <strong>{selectedIsFrom ? <span className="bx-from">from</span> : null}{money(selectedTotal)}</strong>
              </div>
              <span className="bx-count">{selectedCount} {selectedCount === 1 ? "item" : "items"}</span>
            </div>
            <button type="button" className="btn-add-to-cart bx-btn" onClick={addSelected} disabled={!selectedCount}>
              {words.addSelected}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
